/**
 * « Perso », the drawn character of an agent without an avatar image (docs/design/APP-APPLE.md §4), as pure data
 * shared by the app and the Worker: the geometry of the four bodies (rond, fleur, goutte, pilule) in a 100 × 110 box
 * whose body is centred on (50, 54), the seven « Nuit » colors, the « grands yeux », and the static drawing the Worker
 * rasterizes for the contact card (worker/lib/character-image.ts). The app's living character (gaze, blinks, moods:
 * client components/avatar.tsx) draws the same geometry with these constants. Never any global state nor DOM.
 *
 * AgentSettings.character keeps the values of the first design valid (galet, haricot, nuage; terre, miel…): they are
 * shown as the nearest V2 look (normalizeLook), so an older account keeps a face without any migration.
 */
import { CHARACTER_COLORS_V2, CHARACTER_SHAPES_V2, type AgentCharacter } from "./contracts";

export type CharacterShape = (typeof CHARACTER_SHAPES_V2)[number];
export type CharacterColor = (typeof CHARACTER_COLORS_V2)[number];
/** What the drawing shows: one of the four bodies, one of the seven colors. */
export interface CharacterLook {
  shape: CharacterShape;
  color: CharacterColor;
}
/** repos · ecoute (the owner writes) · reflechit (a turn runs) · parle (the reply streams) · joie (done, approved). */
export type CharacterMood = "repos" | "ecoute" | "reflechit" | "parle" | "joie";

export const CHARACTER_SHAPE_LIST: readonly CharacterShape[] = CHARACTER_SHAPES_V2;
export const CHARACTER_COLOR_LIST: readonly CharacterColor[] = CHARACTER_COLORS_V2;

export const CHARACTER_SHAPE_LABEL: Record<CharacterShape, string> = { rond: "Rond", fleur: "Fleur", goutte: "Goutte", pilule: "Pilule" };
export const CHARACTER_COLOR_LABEL: Record<CharacterColor, string> = {
  blanc: "Blanc",
  mandarine: "Mandarine",
  cerise: "Cerise",
  rose: "Rose",
  bleuet: "Bleuet",
  menthe: "Menthe",
  tournesol: "Tournesol",
};
export const CHARACTER_COLOR_HEX: Record<CharacterColor, string> = {
  blanc: "#F5F5F4",
  mandarine: "#FF8A3D",
  cerise: "#F2434E",
  rose: "#FF5FA8",
  bleuet: "#4C7DFF",
  menthe: "#3DD6A0",
  tournesol: "#FFC234",
};

/** The first design's values, shown as the nearest V2 look (colors: nearest in OKLab; ardoise, tied, by hue family). */
const LEGACY_SHAPE: Record<string, CharacterShape> = { galet: "rond", haricot: "pilule", nuage: "fleur", goutte: "goutte" };
const LEGACY_COLOR: Record<string, CharacterColor> = {
  terre: "mandarine",
  miel: "tournesol",
  sauge: "menthe",
  ardoise: "bleuet",
  lavande: "rose",
  cacao: "cerise",
};

const isShape = (v: string): v is CharacterShape => (CHARACTER_SHAPES_V2 as readonly string[]).includes(v);
const isColor = (v: string): v is CharacterColor => (CHARACTER_COLORS_V2 as readonly string[]).includes(v);

/** A stored look (V2 or first-design values) as drawn. */
export function normalizeLook(c: Pick<AgentCharacter, "shape" | "color">): CharacterLook {
  return {
    shape: isShape(c.shape) ? c.shape : (LEGACY_SHAPE[c.shape] ?? "rond"),
    color: isColor(c.color) ? c.color : (LEGACY_COLOR[c.color] ?? "blanc"),
  };
}

/** FNV-1a of the normalized name. */
function nameHash(name: string): number {
  let h = 0x811c9dc5;
  for (const ch of (name.trim() || "Nova").normalize("NFC").toLocaleLowerCase("fr")) h = Math.imul(h ^ ch.codePointAt(0)!, 0x01000193) >>> 0;
  return h >>> 0;
}

/** The main agent's face until one is chosen (« L'agent principal est Blanc par défaut »). */
export const MAIN_DEFAULT_LOOK: CharacterLook = { shape: "rond", color: "blanc" };

/** A look derived from a name (stable: the same name always draws the same character); « Nova » is the white one. */
export function characterLook(name: string): CharacterLook {
  const key = (name.trim() || "Nova").normalize("NFC").toLocaleLowerCase("fr");
  if (key === "nova") return MAIN_DEFAULT_LOOK;
  const h = nameHash(name);
  // Extra agents get a color (white stays the main agent's).
  const colors = CHARACTER_COLORS_V2.filter((c) => c !== "blanc");
  return { shape: CHARACTER_SHAPES_V2[h % CHARACTER_SHAPES_V2.length]!, color: colors[(h >>> 3) % colors.length]! };
}

/**
 * The look shown: the one chosen in « Son visage » (normalized), else the main agent's white « rond », else the one
 * derived from the name (extra agents).
 */
export function lookOf(agent: { name: string; character?: Pick<AgentCharacter, "shape" | "color"> | null; main?: boolean }): CharacterLook {
  if (agent.character) return normalizeLook(agent.character);
  return agent.main ? MAIN_DEFAULT_LOOK : characterLook(agent.name);
}

/** A stable key of what the drawing shows (same key, same picture). */
export function lookKey(look: CharacterLook): string {
  return `${look.shape}-${look.color}`;
}

// ───────────── Geometry (box 100 × 110, body centred on (50, 54)) ─────────────

function polygon(fn: (a: number) => [number, number], n: number): string {
  const pts: string[] = [];
  for (let i = 0; i < n; i++) {
    const [x, y] = fn((i / n) * Math.PI * 2);
    pts.push(`${(50 + x).toFixed(2)} ${(54 + y).toFixed(2)}`);
  }
  return `M${pts.join("L")}Z`;
}

export interface ShapeGeometry {
  /** The body's outline. */
  d: string;
  /** A transform of the body only (the pill leans by −10°); the eyes stay level. */
  transform: string | null;
  /** x of the left and right eyes (y = 54). */
  eyes: [number, number];
}

export const CHARACTER_GEOMETRY: Record<CharacterShape, ShapeGeometry> = {
  // Super-ellipse, exponent 2 / 3.2, radius 38, 96 points.
  rond: {
    d: polygon((a) => {
      const c = Math.cos(a);
      const s = Math.sin(a);
      const e = 2 / 3.2;
      return [Math.sign(c) * Math.abs(c) ** e * 38, Math.sign(s) * Math.abs(s) ** e * 38];
    }, 96),
    transform: null,
    eyes: [41, 59],
  },
  // r(θ) = 36·(1 + 0.1·cos(6θ + π/2)), 144 points.
  fleur: {
    d: polygon((a) => {
      const r = 36 * (1 + 0.1 * Math.cos(6 * a + Math.PI / 2));
      return [Math.cos(a) * r, Math.sin(a) * r];
    }, 144),
    transform: null,
    eyes: [41, 59],
  },
  goutte: { d: "M50 10C64 28 88 42 88 64c0 18-17 30-38 30S12 82 12 64c0-22 24-36 38-54z", transform: null, eyes: [41, 59] },
  pilule: {
    d: "M46 16h8a30 30 0 0 1 30 30v16a30 30 0 0 1-30 30h-8a30 30 0 0 1-30-30V46a30 30 0 0 1 30-30z",
    transform: "rotate(-10 50 54)",
    eyes: [40, 58],
  },
};

/** The eyes' measures (« grands yeux »): white ellipses, dark pupils that look around, a white highlight. */
export const EYE = { y: 54, rx: 7.4, ry: 8.6, pupil: 4.3, pupilColor: "#141414", lookX: 3.4, lookY: 3.6, highlight: 1.5, highlightY: 51.6 } as const;

/** Dark ink on every V2 body (all of them are light enough); white on a dark one. */
export function inkOf(color: CharacterColor): string {
  const hex = CHARACTER_COLOR_HEX[color];
  const n = parseInt(hex.slice(1), 16);
  const lum = (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
  return lum < 0.35 ? "#FFFFFF" : "#141414";
}

/** The happy eyes (two arcs) of a shape, the smile and the talking mouth. */
export function happyEyesPath(shape: CharacterShape): string {
  const [l, r] = CHARACTER_GEOMETRY[shape].eyes;
  return `M${l - 5.5} 56q5.5-8 11 0M${r - 5.5} 56q5.5-8 11 0`;
}
export const SMILE_PATH = "M44 66.5Q50 73 56 66.5";

// ───────────── The static drawing (the contact card) ─────────────

/** One SVG element: attributes under their SVG names (`class`, `stroke-width`…). */
export interface SvgNode {
  tag: string;
  attrs: Record<string, string | number>;
  children?: SvgNode[];
}

const el = (tag: string, attrs: Record<string, string | number>, children?: (SvgNode | null)[]): SvgNode => {
  const kids = children?.filter((c): c is SvgNode => c !== null);
  return kids ? { tag, attrs, children: kids } : { tag, attrs };
};

/** The viewBox of the drawing: the whole character with its shadow, or (`framed`) a square crop for a round photo. */
export function characterViewBox(framed: boolean): string {
  return framed ? "-4 4 108 108" : "0 0 100 110";
}

/** The character at rest, looking straight ahead (`smile`: the little smile of a pointer close to it). */
export function characterNodes(look: CharacterLook, opts: { smile?: boolean; happy?: boolean } = {}): SvgNode[] {
  const g = CHARACTER_GEOMETRY[look.shape];
  const body = CHARACTER_COLOR_HEX[look.color];
  const ink = inkOf(look.color);
  const [l, r] = g.eyes;
  const eyes = opts.happy
    ? el("path", { d: happyEyesPath(look.shape), fill: "none", stroke: ink, "stroke-width": 3.4, "stroke-linecap": "round" })
    : el("g", {}, [
        el("ellipse", { cx: l, cy: EYE.y, rx: EYE.rx, ry: EYE.ry, fill: "#FFFFFF" }),
        el("ellipse", { cx: r, cy: EYE.y, rx: EYE.rx, ry: EYE.ry, fill: "#FFFFFF" }),
        el("circle", { cx: l, cy: EYE.y, r: EYE.pupil, fill: EYE.pupilColor }),
        el("circle", { cx: r, cy: EYE.y, r: EYE.pupil, fill: EYE.pupilColor }),
        el("circle", { cx: l + 1.6, cy: EYE.highlightY, r: EYE.highlight, fill: "#FFFFFF" }),
        el("circle", { cx: r + 1.6, cy: EYE.highlightY, r: EYE.highlight, fill: "#FFFFFF" }),
      ]);
  return [
    el("ellipse", { cx: 50, cy: 104, rx: 26, ry: 3.8, fill: "#000000", opacity: 0.18 }),
    el("path", { d: g.d, fill: body, ...(g.transform ? { transform: g.transform } : {}) }),
    eyes,
    opts.smile || opts.happy ? el("path", { d: SMILE_PATH, fill: "none", stroke: ink, "stroke-width": 2.8, "stroke-linecap": "round" }) : null,
  ].filter((n): n is SvgNode => n !== null);
}

const escapeAttr = (v: string | number): string => String(v).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");

function serialize(node: SvgNode): string {
  const attrs = Object.entries(node.attrs)
    .map(([k, v]) => ` ${k}="${escapeAttr(v)}"`)
    .join("");
  return node.children?.length ? `<${node.tag}${attrs}>${node.children.map(serialize).join("")}</${node.tag}>` : `<${node.tag}${attrs}/>`;
}

/** The background of the contact card's photo: the app's « Nuit ». */
export const CARD_BACKGROUND = "#111111";

/**
 * The character as a standalone square SVG document, framed on the app's night background: the contact card's picture
 * (static: a card needs no animation).
 */
export function characterSvg(look: CharacterLook, opts: { size: number }): string {
  const viewBox = characterViewBox(true);
  const [x, y, w, h] = viewBox.split(" ");
  const body = characterNodes(look).map(serialize).join("");
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${opts.size}" height="${opts.size}" viewBox="${viewBox}">` +
    `<rect x="${x}" y="${y}" width="${w}" height="${h}" fill="${CARD_BACKGROUND}"/>${body}</svg>`
  );
}
